import { Standing } from "../models/Standing";

/** A standings row's zone. "white" means no zone (StandingPage's border check relies on that name). */
export type StandingZone = "green" | "yellow" | "orange" | "red" | "white";

/** Dark-theme fill per zone, as the standings table draws it. */
export const ZONE_COLORS: Record<StandingZone, string> = {
  green: "rgba(0, 128, 0, 0.3)",
  yellow: "rgba(204, 204, 0, 0.25)",
  orange: "rgba(210, 105, 30, 0.3)",
  red: "rgba(139, 0, 0, 0.3)",
  white: "transparent",
};

/** i18n key (pages namespace) naming each zone in a legend. */
export const ZONE_LABEL_KEYS: Record<Exclude<StandingZone, "white">, string> = {
  green: "standings.promotion",
  yellow: "standings.playoff",
  orange: "standings.relegationPlayoff",
  red: "standings.relegation",
};

/**
 * The promotion/relegation zone of the row at `index` (0-based) in a division's table, per season.
 * The one source for these rules: the standings page and the predict pages both read it, so they
 * can no longer drift apart (the predict page used to carry its own stale copy).
 * `standing` is only used for season 31-32 Ligaen, where the zone depended on wins, not position;
 * callers ranking hypothetical positions pass nothing and get no zone there.
 */
export function getStandingZone(season: number, divisionKey: string, index: number, standing?: Standing): StandingZone {
  // Season 31+ color rules
  if (season >= 31) {
    let color: StandingZone = "white";
    if (divisionKey === "ligaen") {
      // Season 33+: top 4 go to the LAN, bottom 4 get no color.
      if (season >= 33) {
        if (index < 4) color = "green";
      } else if (standing && standing.wins >= 3) {
        color = "green";
      }
    } else if (divisionKey === "1div") {
      if (index < 4) color = "green";
      else if (index >= 6 && index <= 7) color = "orange";
      else if (index >= 8 && index <= 9) color = "red";
    } else if (String(divisionKey).startsWith("2div")) {
      if (index === 0) color = "green";
      else if (index === 1) color = "yellow";
      else if (index >= 6 && index <= 7) color = "orange";
      else if (index >= 8 && index <= 9) color = "red";
    } else if (String(divisionKey).startsWith("3div")) {
      if (index === 0) color = "green";
      else if (index >= 1 && index <= 2) color = "yellow";
      else if (index >= 5 && index <= 7) color = "orange";
      else if (index >= 8 && index <= 9) color = "red";
    } else if (String(divisionKey).startsWith("4div")) {
      if (index < 3) color = "green";
      else if (index >= 3 && index < 6) color = "yellow";
    }
    return color;
  }

  if (season === 24) {
    let color: StandingZone = "white";
    if (divisionKey === "ligaen") {
      if (index < 4) color = "green";
      else if (index === 6) color = "orange";
      else if (index === 7) color = "red";
    } else if (divisionKey.endsWith("div")) {
      if (index === 0) color = "green";
      else if (index === 1) color = "yellow";
      else if (index === 6 || index === 7) color = "orange";
      else if (index === 8 || index === 9) color = "red";
    } else if (
      String(divisionKey).startsWith("2div") ||
      String(divisionKey).startsWith("3div") ||
      String(divisionKey).startsWith("4div")
    ) {
      if (index === 0) color = "green";
      else if (index >= 1 && index <= 2) color = "yellow";
      else if (index >= 5 && index <= 7) color = "orange";
      else if (index >= 8 && index <= 9) color = "red";
    } else if (String(divisionKey).startsWith("5div")) {
      if (index === 0) color = "green";
      else if (index >= 1 && index <= 2) color = "yellow";
    }
    return color;
  }

  if (season === 28) {
    let color: StandingZone = "white";
    if (divisionKey === "ligaen") {
      if (index < 4) color = "green";
      else if (index === 7) color = "orange";
      else if (index === 8) color = "red";
    } else if (
      String(divisionKey).startsWith("2div") ||
      divisionKey.endsWith("div")
    ) {
      if (index === 0) color = "green";
      else if (index === 1) color = "yellow";
      else if (index === 6 || index === 7) color = "orange";
      else if (index === 8 || index === 9 || index === 10) color = "red";
    } else if (String(divisionKey).startsWith("3div")) {
      if (index === 0) color = "green";
      else if (index >= 1 && index <= 2) color = "yellow";
      else if (index >= 5 && index <= 7) color = "orange";
      else if (index >= 8 && index <= 9) color = "red";
    } else if (String(divisionKey).startsWith("4div")) {
      if (index === 0 || index === 1) {
        color = "green";
      } else if (index >= 2 && index <= 4) {
        color = "yellow";
      }
    }
    return color;
  }

  // Other seasons
  let color: StandingZone = "white";
  if (divisionKey === "ligaen") {
    if (index < 4) color = "green";
    else if (index === 6) color = "orange";
    else if (index === 7) color = "red";
  } else if (
    String(divisionKey).startsWith("2div") ||
    divisionKey.endsWith("div")
  ) {
    if (index === 0) color = "green";
    else if (index === 1) color = "yellow";
    else if (index === 6 || index === 7) color = "orange";
    else if (index === 8 || index === 9) color = "red";
  } else if (String(divisionKey).startsWith("3div")) {
    if (index === 0) color = "green";
    else if (index >= 1 && index <= 2) color = "yellow";
    else if (index >= 5 && index <= 7) color = "orange";
    else if (index >= 8 && index <= 9) color = "red";
  } else if (String(divisionKey).startsWith("4div")) {
    if (index === 0 || index === 1) {
      color = "green";
    } else if (index >= 2 && index <= 4) {
      color = "yellow";
    }
  }
  return color;
}
